import React, { useEffect, useState, useCallback } from "react"
import { motion } from "framer-motion"
import {
    Chart as ChartJS,
    CategoryScale, LinearScale, BarElement, LineElement, PointElement,
    ArcElement, Title, Tooltip, Legend, Filler,
} from "chart.js"
import { Bar, Line, Doughnut } from "react-chartjs-2"
import {
    HiOutlineUsers, HiOutlineCurrencyRupee, HiOutlineChatBubbleLeftRight,
    HiOutlineArrowTrendingUp, HiOutlineFlag, HiOutlineArrowPath,
    HiOutlineCreditCard, HiOutlineCheckCircle,
} from "react-icons/hi2"
import API from "../../Common/API"

ChartJS.register(
    CategoryScale, LinearScale, BarElement, LineElement, PointElement,
    ArcElement, Title, Tooltip, Legend, Filler
)

// ── palette ──────────────────────────────────────────────────────────────────
const C = {
    prime:   "rgba(185,118,63,1)",
    primeA:  "rgba(185,118,63,0.18)",
    green:   "rgba(26,138,58,1)",
    greenA:  "rgba(26,138,58,0.18)",
    blue:    "rgba(34,94,193,1)",
    blueA:   "rgba(34,94,193,0.18)",
    red:     "rgba(192,74,74,1)",
    redA:    "rgba(192,74,74,0.18)",
    amber:   "rgba(217,119,6,1)",
    amberA:  "rgba(217,119,6,0.18)",
    violet:  "rgba(109,40,217,1)",
    violetA: "rgba(109,40,217,0.18)",
    teal:    "rgba(13,148,136,1)",
    tealA:   "rgba(13,148,136,0.18)",
    PIE: ["rgba(185,118,63,0.85)","rgba(26,138,58,0.85)","rgba(34,94,193,0.85)","rgba(192,74,74,0.85)","rgba(109,40,217,0.85)","rgba(217,119,6,0.85)","rgba(13,148,136,0.85)","rgba(156,163,175,0.85)"],
}

const baseLineOpts = (label) => ({
    responsive: true,
    plugins: { legend: { display: false }, tooltip: { mode: "index", intersect: false } },
    scales: {
        x: { grid: { display: false }, ticks: { font: { size: 11 }, maxRotation: 45 } },
        y: { beginAtZero: true, ticks: { precision: 0, font: { size: 11 } }, grid: { color: "rgba(0,0,0,0.05)" } },
    },
})

const shortDate = (d) => {
    const [, m, day] = d.split("-")
    return `${day}/${m}`
}

// ── KPI card ─────────────────────────────────────────────────────────────────
const KPI = ({ icon: Icon, label, value, sub, color = "text-primeColor", bg = "bg-primeSoft" }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4 rounded-2xl border border-borderColor bg-white p-5 shadow-sm"
    >
        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${bg}`}>
            <Icon className={`h-6 w-6 ${color}`} />
        </div>
        <div className="min-w-0">
            <p className="truncate text-xs font-bold uppercase tracking-[0.14em] text-tpColor">{label}</p>
            <p className="mt-0.5 text-2xl font-bold text-tColor">{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
    </motion.div>
)

// ── Chart card wrapper ────────────────────────────────────────────────────────
const ChartCard = ({ title, sub, children, span = 1 }) => (
    <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className={`rounded-2xl border border-borderColor bg-white p-5 shadow-sm ${span === 2 ? "col-span-1 lg:col-span-2" : ""}`}
    >
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-primeColor">{title}</p>
        {sub && <p className="mt-0.5 text-xs text-slate-400">{sub}</p>}
        <div className="mt-4">{children}</div>
    </motion.div>
)

// ── Empty state ───────────────────────────────────────────────────────────────
const EmptyBar = ({ label }) => (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-slate-300">
        <HiOutlineArrowTrendingUp className="h-8 w-8" />
        <p className="text-xs font-semibold">{label}</p>
    </div>
)

// ── Main component ────────────────────────────────────────────────────────────
const Analytics = () => {
    const [data,    setData]    = useState(null)
    const [loading, setLoading] = useState(true)
    const [days,    setDays]    = useState(30)
    const [error,   setError]   = useState(null)

    const load = useCallback(async (d) => {
        setLoading(true)
        setError(null)
        try {
            const r = await API.get(`/reports/analytics?days=${d}`)
            setData(r.data?.data || null)
        } catch (e) {
            setError("Failed to load analytics.")
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { load(days) }, [days, load])

    const fmt = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n)
    const fmtMoney = (n) => n >= 100000 ? `₹${(n/100000).toFixed(1)}L` : n >= 1000 ? `₹${(n/1000).toFixed(1)}K` : `₹${n}`

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-primeColor">Admin</p>
                    <h2 className="mt-1 text-2xl font-bold text-tColor">Analytics & Reports</h2>
                    <p className="mt-1 text-sm text-slate-400">Platform-wide insights — registrations, revenue, chats, logins and more.</p>
                </div>
                <div className="flex items-center gap-2">
                    {[7, 14, 30, 60, 90].map(d => (
                        <button key={d} type="button"
                            onClick={() => setDays(d)}
                            className={`rounded-full px-3 py-1.5 text-xs font-bold transition ${
                                days === d
                                    ? "bg-primeColor text-white"
                                    : "border border-borderColor bg-white text-tpColor hover:border-primeColor hover:text-primeColor"
                            }`}
                        >{d}d</button>
                    ))}
                    <button type="button" onClick={() => load(days)}
                        className="flex items-center gap-1.5 rounded-full border border-borderColor bg-white px-3 py-1.5 text-xs font-bold text-tpColor hover:border-primeColor hover:text-primeColor transition">
                        <HiOutlineArrowPath className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">{error}</div>
            )}

            {loading && !data && (
                <div className="flex items-center justify-center py-20 text-tpColor text-sm gap-2">
                    <HiOutlineArrowPath className="h-5 w-5 animate-spin text-primeColor" /> Loading analytics…
                </div>
            )}

            {data && (() => {
                const { kpis, dailyRegistrations, dailyLogins, dailyMessages, dailyRevenue, dailyReports,
                        planDistribution, genderDistribution, statusDistribution } = data

                const labels = (dailyRegistrations || []).map(d => shortDate(d.date))

                const hasRevenue = dailyRevenue?.some(d => d.count > 0)
                const hasLogins  = dailyLogins?.some(d => d.count > 0)
                const hasChats   = dailyMessages?.some(d => d.count > 0)
                const hasReports = dailyReports?.some(d => d.count > 0)

                return (
                    <>
                        {/* ── KPI row ── */}
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            <KPI icon={HiOutlineUsers}            label="Total Users"     value={fmt(kpis.totalUsers)}     sub={`${kpis.activeUsers} active`}           color="text-primeColor"  bg="bg-primeSoft" />
                            <KPI icon={HiOutlineCreditCard}       label="Paid Members"    value={fmt(kpis.paidUsers)}      sub={`${Math.round(kpis.paidUsers/Math.max(kpis.totalUsers,1)*100)}% conversion`} color="text-[#1a8a3a]" bg="bg-[#ecf8ef]" />
                            <KPI icon={HiOutlineCurrencyRupee}    label="Est. Revenue"    value={fmtMoney(kpis.totalRevenue)} sub="from active plans"                    color="text-amber-600"   bg="bg-amber-50" />
                            <KPI icon={HiOutlineChatBubbleLeftRight} label="Total Messages" value={fmt(kpis.totalMessages)} sub={`${kpis.totalLogins} logins tracked`}   color="text-[#225ec1]"   bg="bg-[#eef5ff]" />
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            <KPI icon={HiOutlineArrowTrendingUp}  label="Total Logins"    value={fmt(kpis.totalLogins)}    sub="since tracking enabled"                 color="text-violet-600"  bg="bg-violet-50" />
                            <KPI icon={HiOutlineFlag}             label="User Reports"    value={fmt(kpis.totalReports)}   sub={`${kpis.pendingReports} pending`}       color="text-red-500"     bg="bg-red-50" />
                            <KPI icon={HiOutlineCheckCircle}      label="Active Users"    value={fmt(kpis.activeUsers)}    sub="status = Active"                        color="text-teal-600"    bg="bg-teal-50" />
                            <KPI icon={HiOutlineUsers}            label="Free Members"    value={fmt(kpis.totalUsers - kpis.paidUsers)} sub="upgrade opportunity"         color="text-slate-500"   bg="bg-slate-100" />
                        </div>

                        {/* ── Charts grid ── */}
                        <div className="grid gap-5 lg:grid-cols-2">

                            {/* Daily registrations */}
                            <ChartCard title="Daily New Registrations" sub={`Last ${days} days`} span={2}>
                                <Line
                                    data={{
                                        labels,
                                        datasets: [{
                                            label: "New Users",
                                            data: (dailyRegistrations || []).map(d => d.count),
                                            borderColor: C.prime,
                                            backgroundColor: C.primeA,
                                            fill: true,
                                            tension: 0.4,
                                            pointRadius: 3,
                                        }]
                                    }}
                                    options={baseLineOpts("New Users")}
                                    height={80}
                                />
                            </ChartCard>

                            {/* Daily revenue */}
                            <ChartCard title="Estimated Daily Revenue (₹)" sub={`Based on plan subscription dates — last ${days} days`}>
                                {hasRevenue
                                    ? <Bar
                                        data={{
                                            labels,
                                            datasets: [{
                                                label: "Revenue (₹)",
                                                data: (dailyRevenue || []).map(d => d.count),
                                                backgroundColor: C.greenA,
                                                borderColor: C.green,
                                                borderWidth: 2,
                                                borderRadius: 6,
                                            }]
                                        }}
                                        options={baseLineOpts("Revenue")}
                                        height={160}
                                      />
                                    : <EmptyBar label="No revenue data in this period" />
                                }
                            </ChartCard>

                            {/* Daily logins */}
                            <ChartCard title="Daily Logins" sub={`Last ${days} days — tracked from first login after deploy`}>
                                {hasLogins
                                    ? <Line
                                        data={{
                                            labels,
                                            datasets: [{
                                                label: "Logins",
                                                data: (dailyLogins || []).map(d => d.count),
                                                borderColor: C.violet,
                                                backgroundColor: C.violetA,
                                                fill: true,
                                                tension: 0.4,
                                                pointRadius: 3,
                                            }]
                                        }}
                                        options={baseLineOpts("Logins")}
                                        height={160}
                                      />
                                    : <EmptyBar label="Login tracking starts after next backend restart" />
                                }
                            </ChartCard>

                            {/* Daily chats */}
                            <ChartCard title="Daily Messages Sent" sub={`Last ${days} days`}>
                                {hasChats
                                    ? <Bar
                                        data={{
                                            labels,
                                            datasets: [{
                                                label: "Messages",
                                                data: (dailyMessages || []).map(d => d.count),
                                                backgroundColor: C.blueA,
                                                borderColor: C.blue,
                                                borderWidth: 2,
                                                borderRadius: 6,
                                            }]
                                        }}
                                        options={baseLineOpts("Messages")}
                                        height={160}
                                      />
                                    : <EmptyBar label="No chat messages yet — start chatting!" />
                                }
                            </ChartCard>

                            {/* Daily user reports */}
                            <ChartCard title="Daily User Abuse Reports" sub={`Last ${days} days`}>
                                {hasReports
                                    ? <Bar
                                        data={{
                                            labels,
                                            datasets: [{
                                                label: "Reports",
                                                data: (dailyReports || []).map(d => d.count),
                                                backgroundColor: C.redA,
                                                borderColor: C.red,
                                                borderWidth: 2,
                                                borderRadius: 6,
                                            }]
                                        }}
                                        options={baseLineOpts("Reports")}
                                        height={160}
                                      />
                                    : <EmptyBar label="No reports filed in this period" />
                                }
                            </ChartCard>

                            {/* Plan distribution donut */}
                            <ChartCard title="Plan Distribution" sub="All-time paid vs free members">
                                {planDistribution?.length > 0
                                    ? <div className="flex items-center gap-4 flex-wrap">
                                        <div className="w-48 mx-auto">
                                            <Doughnut
                                                data={{
                                                    labels: planDistribution.map(p => p.name),
                                                    datasets: [{
                                                        data: planDistribution.map(p => p.count),
                                                        backgroundColor: C.PIE,
                                                        borderWidth: 2,
                                                        borderColor: "#fff",
                                                    }]
                                                }}
                                                options={{ plugins: { legend: { position: "bottom", labels: { font: { size: 11 }, padding: 10 } } }, cutout: "65%" }}
                                            />
                                        </div>
                                        <ul className="space-y-2 text-sm flex-1">
                                            {planDistribution.map((p, i) => (
                                                <li key={p.name} className="flex items-center justify-between gap-2">
                                                    <span className="flex items-center gap-2">
                                                        <span className="h-3 w-3 rounded-full shrink-0" style={{ background: C.PIE[i % C.PIE.length] }} />
                                                        <span className="font-semibold text-tColor">{p.name}</span>
                                                    </span>
                                                    <span className="font-bold text-tpColor">{p.count}</span>
                                                </li>
                                            ))}
                                        </ul>
                                      </div>
                                    : <EmptyBar label="No plan data" />
                                }
                            </ChartCard>

                            {/* Gender distribution donut */}
                            <ChartCard title="Gender Distribution" sub="All registered users">
                                {genderDistribution?.length > 0
                                    ? <div className="flex items-center gap-4 flex-wrap">
                                        <div className="w-48 mx-auto">
                                            <Doughnut
                                                data={{
                                                    labels: genderDistribution.map(g => g.name),
                                                    datasets: [{
                                                        data: genderDistribution.map(g => g.count),
                                                        backgroundColor: [C.blue, C.red, C.amber, C.green].slice(0, genderDistribution.length),
                                                        borderWidth: 2,
                                                        borderColor: "#fff",
                                                    }]
                                                }}
                                                options={{ plugins: { legend: { position: "bottom", labels: { font: { size: 11 }, padding: 10 } } }, cutout: "65%" }}
                                            />
                                        </div>
                                        <ul className="space-y-2 text-sm flex-1">
                                            {genderDistribution.map((g, i) => (
                                                <li key={g.name} className="flex items-center justify-between gap-2">
                                                    <span className="flex items-center gap-2">
                                                        <span className="h-3 w-3 rounded-full shrink-0" style={{ background: [C.blue, C.red, C.amber, C.green][i] }} />
                                                        <span className="font-semibold text-tColor">{g.name}</span>
                                                    </span>
                                                    <span className="font-bold text-tpColor">{g.count}</span>
                                                </li>
                                            ))}
                                        </ul>
                                      </div>
                                    : <EmptyBar label="No gender data" />
                                }
                            </ChartCard>

                            {/* Status distribution bar */}
                            <ChartCard title="Profile Status Breakdown" sub="All users by approval status" span={2}>
                                {statusDistribution?.length > 0
                                    ? <Bar
                                        data={{
                                            labels: statusDistribution.map(s => s.name),
                                            datasets: [{
                                                label: "Users",
                                                data: statusDistribution.map(s => s.count),
                                                backgroundColor: [C.amberA, C.greenA, C.blueA, C.redA, C.tealA, C.violetA],
                                                borderColor:      [C.amber,  C.green,  C.blue,  C.red,  C.teal,  C.violet],
                                                borderWidth: 2,
                                                borderRadius: 8,
                                            }]
                                        }}
                                        options={{
                                            ...baseLineOpts("Status"),
                                            plugins: { legend: { display: false }, tooltip: { mode: "index", intersect: false } },
                                        }}
                                        height={70}
                                      />
                                    : <EmptyBar label="No status data" />
                                }
                            </ChartCard>
                        </div>

                        {/* ── Footer note ── */}
                        <p className="text-center text-xs text-slate-300 pb-4">
                            Revenue is estimated from plan rate × active subscribers. Login tracking begins after backend restart.
                        </p>
                    </>
                )
            })()}
        </div>
    )
}

export default Analytics
